/** @forge-header
 * @id          boundaryLog
 * @role        Channeled diagnostic logger | structured console + relay persistence, ~65 production call sites
 * @pattern     pure-fn
 * @purity      api
 * @units       n/a
 * @upstream    infra/clientLog:sendClientLog
 * @downstream  ~16 files across kalshiApi, market, audio, infra, trading
 * @exports     BoundaryChannel:type | boundaryWarn:fn | boundaryError:fn
 * @test        untested (indirect only)
 * @fan-out     ~30
 * @invariant   CHANNEL_ENUM | new channels must be added to BoundaryChannel union type AND lib/infra/_CONTEXT.md manifest
 */

import { sendClientLog } from './clientLog';

// @ai-note: Adding a channel? Update this union type AND the lib/infra/_CONTEXT.md manifest.
export type BoundaryChannel =
  | 'api-shape'
  | 'price-unit'
  | 'stream'
  | 'hydration'
  | 'sound'
  | 'auth'
  | 'order'
  | 'connection'
  | 'position'
  | 'defense';

function formatMsg(channel: BoundaryChannel, msg: string): string {
  return `[boundary:${channel}] ${msg}`;
}

export function boundaryWarn(
  channel: BoundaryChannel,
  msg: string,
  ctx?: Record<string, unknown>
): void {
  const formatted = formatMsg(channel, msg);
  console.warn(formatted, ctx ?? '');
  sendClientLog('warn', formatted, { source: `boundary:${channel}`, context: ctx });
}

export function boundaryError(
  channel: BoundaryChannel,
  msg: string,
  ctx?: Record<string, unknown>
): void {
  const formatted = formatMsg(channel, msg);
  console.error(formatted, ctx ?? '');
  sendClientLog('error', formatted, { source: `boundary:${channel}`, context: ctx });
}

/** @forge-footer
 * @checklist
 *   1. CHANNEL | adding a channel? Update BoundaryChannel union type AND lib/infra/_CONTEXT.md manifest
 *   2. FIRE_AND_FORGET | sendClientLog is rate-limited and non-blocking — never await it
 * @sync-group none
 */
